/**
 * Spec Export Pipeline — prepares a set of ProjectSpecs for export by
 * resolving every `smart-token` block into actual ContentBlock[] using
 * the project's context (other specs, team, client, current issuance).
 *
 * This lives on the renderer side because that's where all the context
 * data is hydrated. The main process gets clean, fully-resolved blocks.
 *
 * Usage:
 *   const payloads = await prepareSpecsForExport(projectId, targetSpecs);
 *   await window.api.exportProjectSpecs({ specs: payloads, ... });
 */

import { publishedView } from '../../shared/spec-publish';
import type {
  ContentBlock,
  ProjectSpec,
  Organization,
  Issuance,
  SmartBlock,
  StandardConfig,
  RevisionSettings,
} from '../../shared/types';
import {
  buildProjectContext,
  expandSmartTokens,
  BUILT_IN_SMART_BLOCKS,
} from '../../engine/smart-blocks';
import { stripMembership } from '../../shared/spec-compose';
import { DEFAULT_REVISION_SETTINGS, realIssuances } from '../../shared/types';
import { applyRevisionMarks } from './specService';
import { SmartBlockService } from './smartBlockService';
import { getAuthToken } from './authService';
import { API_ORIGIN } from '../lib/apiBase';

interface PreparedSpec {
  csiNumber?: string;
  title?: string;
  blocks: ContentBlock[];
  /** Set when markRevisions found a baseline — styling for the marks. */
  revisionSettings?: RevisionSettings;
}

/** The issuance whose snapshot is the current revision-mark baseline.
 *  Auto safety snapshots (kind:'auto' — the Designer's rolling design-phase
 *  history) are never a baseline. */
export function revisionBaseline(spec: ProjectSpec): Issuance | null {
  const withSnap = realIssuances(spec.issuances).filter((i) => i.snapshot);
  return withSnap.length ? withSnap[withSnap.length - 1] : null;
}

/** Flatten a snapshot (or a live spec) to the block list exports compare. */
function flattenForDiff(src: { coreContent?: ContentBlock[]; systems?: any[] }): ContentBlock[] {
  return [
    ...(src.coreContent || []),
    ...((src.systems || []).flatMap((sys: any) => sys.content || []) as ContentBlock[]),
  ].filter((b) => !b.excluded);
}

interface PrepareInputs {
  project: {
    id: string;
    name?: string;
    number?: string;
    address?: string;
    phase?: string;
    clientId?: string;
    [k: string]: any;
  };
  allProjectSpecs: ProjectSpec[];
  team?: Array<{
    id: string;
    name?: string;
    email?: string;
    role?: string;
    title?: string;
  }>;
  client?: Organization;
}

export async function prepareSpecsForExport(
  selectedSpecs: ProjectSpec[],
  inputs: PrepareInputs,
  opts?: {
    /** Mark changes since each spec's latest issuance snapshot (architect
     *  revision marks). Specs with no snapshot export unmarked. */
    markRevisions?: boolean;
  }
): Promise<PreparedSpec[]> {
  // Fetch firm-published smart blocks once for the whole batch.
  let firmCatalog: SmartBlock[] = [];
  try {
    firmCatalog = await SmartBlockService.list();
  } catch {
    // export still proceeds with built-ins only
  }
  const catalog: SmartBlock[] = [...BUILT_IN_SMART_BLOCKS, ...firmCatalog];

  const resolvedAt = new Date().toISOString();
  const out: PreparedSpec[] = [];

  for (const spec of selectedSpecs) {
    // The ladder stops at the fork: a project spec is already composed, but the
    // engine must never meet a membership marker, so the ONE brain's strip law
    // runs here too (docs/MASTERS_LADDER_PLAN.md §R5.2 — same rule as
    // `toExportBlocks`, applied to an already-flat list).
    // Publish gate (SYNC D12): exports read the PUBLISHED view; a spec that
    // was never published exports its live content.
    const view = publishedView(spec);
    let blocksIn: ContentBlock[] = stripMembership([
      ...view.coreContent,
      ...(view.systems.flatMap((sys: any) => sys.content || []) as ContentBlock[]),
    ]);

    // Revision marks: diff against the latest issuance snapshot BEFORE smart
    // tokens expand (raw block IDs are stable; expanded blocks are not).
    let specRevisionSettings: RevisionSettings | undefined;
    if (opts?.markRevisions) {
      const baseline = revisionBaseline(spec);
      if (baseline?.snapshot) {
        blocksIn = applyRevisionMarks(flattenForDiff(baseline.snapshot), flattenForDiff(view));
        specRevisionSettings = spec.revisionSettings || DEFAULT_REVISION_SETTINGS;
      }
    }

    // Build context once per spec — `otherSpecsInProject` is every OTHER
    // spec in the same project, not just the selected ones, so Related
    // Sections lists the full set even when only one is being exported.
    const ctx = buildProjectContext({
      project: inputs.project,
      thisSpec: {
        id: spec.id,
        masterId: spec.masterId,
        csiNumber: spec.csiNumber,
        sectionNumber: spec.csiNumber,
        title: spec.title,
        coreContent: blocksIn,
      },
      allProjectSpecs: inputs.allProjectSpecs,
      team: inputs.team ?? [],
      client: inputs.client
        ? {
            id: inputs.client.id,
            name: inputs.client.name,
            address: (inputs.client as any).address,
          }
        : undefined,
      currentIssuance: realIssuances(spec.issuances).slice(-1)[0] as Issuance | undefined,
      resolvedAt,
    });

    const expanded = expandSmartTokens(blocksIn, catalog, ctx);
    out.push({
      csiNumber: spec.csiNumber,
      title: spec.title,
      blocks: expanded,
      ...(specRevisionSettings ? { revisionSettings: specRevisionSettings } : {}),
    });
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════
   Web export — server-side DOCX generation + browser download.

   The browser can't run the local Word engine, so the SAME payload the
   desktop path hands to `window.api.exportProjectSpecs` is POSTed to the
   EC2 endpoint instead:

     POST {apiBase}/api/export-specs  (Bearer — same token as apiClient)
     Body:     { forma, sections, projectMeta, facets? }
     Response: { files: [{ name, dataBase64 } | { name, error }] }

   Per-spec forma inheritance is preserved by grouping specs exactly the
   way main.ts `engine:exportProjectSpecs` does (effective forma = the
   spec's own copy when present, else the batch forma; metaOverrides merge
   over projectMeta) — one POST per group, one forma per request as the
   endpoint contract requires.
   ═══════════════════════════════════════════════════════════════ */

/** Same element type as the `specs` array of `window.api.exportProjectSpecs`. */
interface WebExportSpec {
  csiNumber?: string;
  title?: string;
  blocks: ContentBlock[];
  /** Per-spec forma (specs-style inheritance) — wins over the batch formaConfig. */
  formaConfig?: StandardConfig;
  /** Per-spec token values picked in the rendered view — merged over projectMeta. */
  metaOverrides?: Record<string, string>;
  /** Revision-mark styling — applied when blocks carry `revision` info. */
  revisionSettings?: RevisionSettings;
}

/**
 * Expected output filename — mirrors `specFilename()` in
 * `src/engine/spec-docx-builder.ts` (not imported: that module drags the
 * whole `docx` package into the renderer bundle). Used only to report
 * per-file failures when an entire request fails.
 */
function webSpecFilename(spec: { csiNumber?: string; title?: string }): string {
  const base = [spec.csiNumber, spec.title].filter(Boolean).join(' - ') || 'spec';
  const safe = base
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return `${safe}.docx`;
}

function base64ToDocxBlob(dataBase64: string): Blob {
  const bin = atob(dataBase64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

function triggerDownload(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser time to start the download before releasing the URL.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Web counterpart of `window.api.exportProjectSpecs` (docx only — PDF is
 * desktop-only). Takes the exact same `specs`/`formaConfig`/`projectMeta`
 * the desktop call takes and returns the same `{ docx: [...] }` result
 * shape, so the export modal's success/failure handling is shared verbatim.
 * Files land as browser downloads instead of in an output folder.
 */
export async function exportSpecsViaServer(args: {
  specs: WebExportSpec[];
  formaConfig: StandardConfig | null;
  projectMeta?: Record<string, string>;
  facets?: Partial<Record<'body' | 'headers' | 'footers' | 'prePost' | 'pageSetup', boolean>>;
}): Promise<{
  docx: Array<{ file: string; success: boolean; error?: string }>;
  pdf: never[];
  /** Non-fatal notes the server attached to the run (shown with failures). */
  warnings?: string[];
}> {
  const { specs, formaConfig, projectMeta, facets } = args;
  if (!specs?.length) return { docx: [], pdf: [] };

  const token = getAuthToken();
  if (!token) throw new Error('Not signed in');

  // Group by effective forma + token meta — IDENTICAL semantics to the
  // grouping in main.ts `engine:exportProjectSpecs` (Stage 2).
  const groups = new Map<
    string,
    { forma: StandardConfig | null; meta?: Record<string, string>; specs: WebExportSpec[] }
  >();
  for (const s of specs) {
    const eff = s.formaConfig !== undefined ? s.formaConfig : formaConfig;
    const meta = s.metaOverrides ? { ...(projectMeta || {}), ...s.metaOverrides } : projectMeta;
    const key = JSON.stringify([(eff as any)?.id ?? null, meta ?? null]);
    let g = groups.get(key);
    if (!g) {
      g = { forma: eff ?? null, meta, specs: [] };
      groups.set(key, g);
    }
    g.specs.push(s);
  }

  const results: Array<{ file: string; success: boolean; error?: string }> = [];
  const warnings: string[] = [];
  const downloads: Array<{ name: string; blob: Blob }> = [];

  for (const g of groups.values()) {
    try {
      const response = await fetch(`${API_ORIGIN}/api/export-specs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          forma: g.forma,
          sections: g.specs.map((s) => ({
            csiNumber: s.csiNumber,
            title: s.title,
            blocks: s.blocks,
            ...(s.revisionSettings ? { revisionSettings: s.revisionSettings } : {}),
          })),
          projectMeta: g.meta ?? {},
          ...(facets ? { facets } : {}),
        }),
      });
      if (!response.ok) {
        let msg = `Export failed (HTTP ${response.status})`;
        try {
          const err = await response.json();
          if (err?.error) msg = String(err.error);
        } catch {
          /* non-JSON error body */
        }
        throw new Error(msg);
      }
      const data: {
        files?: Array<{ name: string; dataBase64?: string; error?: string; warning?: string }>;
        warning?: string;
      } = await response.json();
      if (data.warning) warnings.push(String(data.warning));
      for (const f of data.files ?? []) {
        if (f.warning) warnings.push(`${f.name}: ${f.warning}`);
        if (f.dataBase64) {
          downloads.push({ name: f.name, blob: base64ToDocxBlob(f.dataBase64) });
          results.push({ file: f.name, success: true });
        } else {
          results.push({ file: f.name, success: false, error: f.error || 'Unknown export error' });
        }
      }
    } catch (err: any) {
      // Whole-request failure — surface it per file, like desktop failures.
      for (const s of g.specs) {
        results.push({
          file: webSpecFilename(s),
          success: false,
          error: err?.message || String(err),
        });
      }
    }
  }

  // Sequential downloads with a small gap so the browser doesn't drop any.
  for (let i = 0; i < downloads.length; i++) {
    if (i > 0) await new Promise((r) => setTimeout(r, 350));
    triggerDownload(downloads[i].name, downloads[i].blob);
  }

  return { docx: results, pdf: [], ...(warnings.length ? { warnings } : {}) };
}
